import { Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { NetworkTab, NetworkTabView, NetworkView, NetworkWindowKind, SplitMap, UsdtPartyRow, WatchTargetRow } from "../api/types.js";
import { count, fromUtcInput, toLocalInput, trx, utc } from "../format.js";
import { downloadCsv, toCsv } from "../lib/csv.js";
import type { Router } from "../router.js";
import { Address, Button, ErrorNote, Note, Panel, Pill, ScreenHeader, Spinner, StatRow, Table, Td, Th, Tile, inputClass } from "../ui.js";

/**
 * Network (mocks docs/product/mocks/admin-network-mock.html and admin-network-tabs-mock.html, approved by the owner
 * 2026-10-18): every TRON block as our own chain analyzer reads it, in a rolling window, one tab per question.
 * ACCESS: the whole page is viewer (owner 2026-10-18: one admin account exists, roles get split before a second one).
 * The tab is the path (`/network/burners`); the window, the filter and the address card's address are the query, so
 * a view can be sent as a link. A query whose read failed says so in place; the rest of the tab stands.
 */

const TABS: ReadonlyArray<{ id: Exclude<NetworkTab, "provider"> | "reports"; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "demand", label: "Demand by hour" },
  { id: "providers", label: "Providers" },
  { id: "burners", label: "Burners" },
  { id: "usdt", label: "USDT flows" },
  { id: "activations", label: "Activations" },
  { id: "address", label: "Address" },
  { id: "watch", label: "Watch" },
  { id: "reports", label: "Reports" },
];
type TabId = (typeof TABS)[number]["id"];

const WINDOWS: ReadonlyArray<{ id: NetworkWindowKind; label: string }> = [
  { id: "24h", label: "24 h" },
  { id: "7d", label: "7 d" },
  { id: "30d", label: "30 d" },
  { id: "today", label: "Today UTC" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom…" },
];

const USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
const TRON_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const HOUR_MS = 3_600_000;

interface ViewState {
  window: NetworkWindowKind;
  /** ISO; only for a custom window. */
  from: string;
  to: string;
  /** Filter: part of an address or a service name. */
  q: string;
  /** Filter: the least amount a row shows, in TRX or USDT (the table's own unit). */
  min: string;
  /** The address card's address. */
  address: string;
}

function readSearch(): ViewState {
  const p = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  const kind = p.get("window");
  return {
    window: WINDOWS.some((w) => w.id === kind) ? (kind as NetworkWindowKind) : "24h",
    from: p.get("from") ?? "",
    to: p.get("to") ?? "",
    q: p.get("q") ?? "",
    min: p.get("min") ?? "",
    address: p.get("address") ?? "",
  };
}

function writeSearch(v: ViewState, tab: TabId): string {
  const p = new URLSearchParams();
  if (v.window !== "24h") p.set("window", v.window);
  if (v.window === "custom" && v.from !== "") p.set("from", v.from);
  if (v.window === "custom" && v.to !== "") p.set("to", v.to);
  if (v.q !== "") p.set("q", v.q);
  if (v.min !== "") p.set("min", v.min);
  if (tab === "address" && v.address !== "") p.set("address", v.address);
  const s = p.toString();
  return s === "" ? "" : `?${s}`;
}

export function NetworkScreen({ router }: { router: Router }) {
  const param = router.route.param;
  const tab: TabId = TABS.find((t) => t.id === param)?.id ?? "overview";
  const [view, setView] = useState<ViewState>(readSearch);

  // The query follows the state; the tab is the path, set by the router.
  useEffect(() => {
    const search = writeSearch(view, tab);
    if (window.location.search !== search) window.history.replaceState(null, "", `${window.location.pathname}${search}`);
  }, [view, tab]);

  const open = (id: TabId) => router.navigate("network", id === "overview" ? undefined : id);
  const openCard = (address: string) => {
    setView((v) => ({ ...v, address }));
    open("address");
  };

  return (
    <>
      <ScreenHeader
        title="Network"
        lead='Every TRON block, read by our own collector, counted once 19 blocks deep (~1 min). The window rolls: "24 h" is the last 28 800 blocks, compared with the 24 h before them. Times are UTC.'
      />
      <WindowBar view={view} onChange={(next) => setView((v) => ({ ...v, ...next }))} />
      <nav class="mb-3 flex flex-wrap gap-1 border-b border-line" aria-label="Network tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => open(t.id)}
            class={`-mb-px border-b-2 px-3 py-2 text-[12.5px] font-semibold ${t.id === tab ? "border-accent text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <FilterBar view={view} onChange={(next) => setView((v) => ({ ...v, ...next }))} onOpenCard={openCard} />
      {tab === "reports" ? <ReportsTab /> : <TabBody tab={tab} view={view} onOpenCard={openCard} />}
    </>
  );
}

function WindowBar({ view, onChange }: { view: ViewState; onChange: (next: Partial<ViewState>) => void }) {
  const [from, setFrom] = useState(view.from === "" ? "" : toLocalInput(view.from));
  const [to, setTo] = useState(view.to === "" ? "" : toLocalInput(view.to));
  return (
    <div class="mb-3 flex flex-wrap items-center gap-2">
      <div class="inline-flex gap-0.5 rounded-[12px] border border-line bg-surface p-[3px]" role="group" aria-label="Window">
        {WINDOWS.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => onChange({ window: w.id })}
            class={`rounded-[9px] px-2.5 py-1 text-[12px] ${view.window === w.id ? "bg-surface-raised text-ink" : "text-ink-muted hover:text-ink"}`}
          >
            {w.label}
          </button>
        ))}
      </div>
      {view.window !== "custom" ? null : (
        <form
          class="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onChange({ from: fromUtcInput(from) ?? "", to: fromUtcInput(to) ?? "" });
          }}
        >
          <input type="datetime-local" class={`${inputClass} w-auto`} value={from} onInput={(e) => setFrom((e.currentTarget as HTMLInputElement).value)} aria-label="From, UTC" />
          <span class="text-[11px] text-ink-muted">to</span>
          <input type="datetime-local" class={`${inputClass} w-auto`} value={to} onInput={(e) => setTo((e.currentTarget as HTMLInputElement).value)} aria-label="To, UTC (empty = now)" />
          <Button tone="ghost" type="submit">
            Show
          </Button>
          <span class="text-[11px] text-ink-muted">UTC; at most 30 days; an empty end is now.</span>
        </form>
      )}
    </div>
  );
}

function FilterBar({ view, onChange, onOpenCard }: { view: ViewState; onChange: (next: Partial<ViewState>) => void; onOpenCard: (address: string) => void }) {
  const q = view.q.trim();
  return (
    <div class="mb-3 flex flex-wrap items-center gap-2 rounded-[16px] border border-line bg-surface px-3 py-2.5">
      <span class="text-[11px] text-ink-muted">Filter</span>
      <input class={`${inputClass} w-64`} placeholder="Address or service name" value={view.q} onInput={(e) => onChange({ q: (e.currentTarget as HTMLInputElement).value })} />
      <input class={`${inputClass} w-32`} placeholder="Min amount" inputMode="decimal" value={view.min} onInput={(e) => onChange({ min: (e.currentTarget as HTMLInputElement).value })} />
      {TRON_ADDRESS.test(q) ? (
        <Button tone="ghost" onClick={() => onOpenCard(q)}>
          Open address card
        </Button>
      ) : null}
      <span class="text-[11px] text-ink-muted">Applies to every tab's tables; min amount is in the table's unit (TRX or USDT). The link keeps it.</span>
    </div>
  );
}

/** The row filter of the bar above: a text match and a least amount. */
function rowFilter(view: ViewState) {
  const q = view.q.trim().toLowerCase();
  const min = view.min.trim() === "" ? null : Number(view.min);
  return (text: string, amount: number | null) => (q === "" || text.toLowerCase().includes(q)) && (min === null || !Number.isFinite(min) || amount === null || amount >= min);
}

/** The window as the tab reads take it. */
const windowQuery = (view: ViewState) => ({
  window: view.window,
  from: view.window === "custom" ? view.from : undefined,
  to: view.window === "custom" && view.to !== "" ? view.to : undefined,
});

function TabBody({ tab, view, onOpenCard }: { tab: Exclude<NetworkTab, "provider">; view: ViewState; onOpenCard: (address: string) => void }) {
  const address = tab === "address" ? view.address : "";
  const ready = (view.window !== "custom" || view.from !== "") && (tab !== "address" || TRON_ADDRESS.test(address));
  const res = useResource<NetworkTabView | null>(
    (signal) => {
      void signal;
      if (!ready) return Promise.resolve(null);
      return adminReads.networkTab(tab, { ...windowQuery(view), address: tab === "address" ? address : undefined });
    },
    [tab, view.window, view.from, view.to, address, ready],
  );

  if (tab === "address" && !TRON_ADDRESS.test(address)) {
    return <Note>Type a T-address in the filter and open its card, or open one from any table.</Note>;
  }
  if (!ready) return <Note>Pick the start of the custom window above.</Note>;
  if (res.data == null) return res.error !== null ? <ErrorNote error={res.error} onRetry={() => res.reload()} /> : <Spinner />;
  const data = res.data;
  if (!data.connected) return <Note tone="warning">The chain analyzer is not reachable from here: {Object.values(data.errors)[0] ?? "no answer"}.</Note>;
  const ctx: TabContext = { data, view, keep: rowFilter(view), onOpenCard };
  return (
    <div class="space-y-3">
      <p class="text-[11.5px] text-ink-muted">
        Window {utc(data.window.from)} – {utc(data.window.to)}, as of {utc(data.as_of)}
        {res.loading ? " · updating…" : ""}
      </p>
      {tab === "overview" ? <OverviewTab {...ctx} /> : null}
      {tab === "demand" ? <DemandTab {...ctx} /> : null}
      {tab === "providers" ? <ProvidersTab {...ctx} /> : null}
      {tab === "burners" ? <BurnersTab {...ctx} /> : null}
      {tab === "usdt" ? <UsdtTab {...ctx} /> : null}
      {tab === "activations" ? <ActivationsTab {...ctx} /> : null}
      {tab === "address" ? <AddressTab {...ctx} /> : null}
      {tab === "watch" ? <WatchTab {...ctx} /> : null}
    </div>
  );
}

interface TabContext {
  data: NetworkTabView;
  view: ViewState;
  keep: (text: string, amount: number | null) => boolean;
  onOpenCard: (address: string) => void;
}

// ---- shared bits -------------------------------------------------------------------------------

const COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const sp = (m: SplitMap | undefined, key: string): number => m?.[key] ?? 0;
const pct = (part: number | null | undefined, whole: number | null | undefined): string | null =>
  part == null || whole == null || whole === 0 ? null : `${((100 * part) / whole).toFixed(1)} %`;
const change = (now: number | null | undefined, before: number | null | undefined): string | null =>
  now == null || before == null || before === 0 ? null : `${now >= before ? "+" : ""}${((100 * (now - before)) / before).toFixed(1)} %`;
/** 6-decimal USDT units as dollars, compact: "$ 24.1B". */
const usd = (units: string | null | undefined): string => (units == null ? "—" : `$ ${COMPACT.format(Number(units) / 1e6)}`);
const usdNumber = (units: string | null | undefined): number | null => (units == null ? null : Number(units) / 1e6);
const trxCompact = (sun: number | null | undefined): string => (sun == null ? "—" : `${COMPACT.format(sun / 1e6)} TRX`);
const hh = (hour: number) => String(hour).padStart(2, "0");
/** An ISO hour as "10-18 08". */
const dayHour = (iso: string | null | undefined): string => (iso == null ? "—" : `${iso.slice(5, 10)} ${iso.slice(11, 13)}`);

function Failed({ q, errors, stale }: { q: string | string[]; errors: Partial<Record<string, string>>; stale?: Partial<Record<string, string>> | undefined }) {
  const keys = Array.isArray(q) ? q : [q];
  const reason = keys.map((k) => errors[k]).find((r) => r !== undefined);
  if (reason !== undefined) return <Note tone="warning">This block could not be read: {reason}.</Note>;
  const at = keys.map((k) => stale?.[k]).find((r) => r !== undefined);
  return at === undefined ? null : <Note>Shown as read at {utc(at)} UTC: the analyzer did not answer just now.</Note>;
}

/** An address cell: the address, and the card it opens. */
function AddressCell({ value, sub, onOpenCard }: { value: string; sub?: string | undefined; onOpenCard: (address: string) => void }) {
  return (
    <>
      <span class="inline-flex items-center gap-1.5">
        <Address value={value} />
        <button type="button" class="text-[11px] text-accent-text hover:underline" onClick={() => onOpenCard(value)}>
          card
        </button>
      </span>
      {sub === undefined ? null : <span class="block text-[11px] text-ink-muted">{sub}</span>}
    </>
  );
}

/** A provider is a service name, or the delegator's address while the registry does not know it. */
function PartyCell({ name, sub, onOpenCard }: { name: string; sub?: string | undefined; onOpenCard: (address: string) => void }) {
  if (TRON_ADDRESS.test(name)) return <AddressCell value={name} sub={sub ?? "unlabelled"} onOpenCard={onOpenCard} />;
  return (
    <>
      <span class="font-medium">{name}</span>
      {sub === undefined ? null : <span class="block text-[11px] text-ink-muted">{sub}</span>}
    </>
  );
}

function Stack({ parts }: { parts: ReadonlyArray<{ label: string; value: number; tone: string; note?: string }> }) {
  const total = parts.reduce((t, p) => t + p.value, 0);
  return (
    <>
      <div class="mb-2.5 flex h-[26px] overflow-hidden rounded-[8px] bg-surface-raised">
        {total === 0 ? null : parts.map((p) => <i key={p.label} class={`block h-full ${p.tone}`} style={{ width: `${(100 * p.value) / total}%` }} title={p.label} />)}
      </div>
      <div class="grid gap-x-4 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
        {parts.map((p) => (
          <div key={p.label} class="flex justify-between gap-2 text-[12px]">
            <span>
              <b class={`mr-1.5 inline-block size-[9px] rounded-[3px] ${p.tone}`} />
              {p.label}
            </span>
            <span class="num text-ink-muted">
              {count(p.value)} · {pct(p.value, total) ?? "—"}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function Spark({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <span class="inline-flex h-[18px] items-end gap-px align-middle">
      {values.map((v, i) => (
        <i key={i} class="block w-[3px] rounded-[1px] bg-accent-text" style={{ height: `${Math.max(6, (100 * v) / max)}%` }} />
      ))}
    </span>
  );
}

function SplitCoverage({ blocks, split }: { blocks: number | null | undefined; split: number | null | undefined }) {
  if (blocks == null || split == null || split >= blocks) return null;
  return (
    <Note tone="warning">
      The split figures cover {count(split)} of {count(blocks)} blocks: blocks read before the new counters (18 Oct) have no split until the history load
      reads them again.
    </Note>
  );
}

// ---- Overview ----------------------------------------------------------------------------------

function OverviewTab({ data }: TabContext) {
  const cur = data.data.t1?.find((r) => r.current === 1);
  const prev = data.data.t1?.find((r) => r.current === 0);
  const s = cur?.split;
  const t2 = data.data.t2?.[0];
  const energyUsed = sp(s, "energy_from_stake") + sp(s, "energy_from_origin") + sp(s, "energy_burned");
  const needEnergy = sp(s, "energy_covered_tx") + sp(s, "energy_partial_tx") + sp(s, "energy_burned_tx");
  const noEnergy = sp(s, "noenergy_tx");
  return (
    <>
      <Failed q={["t1", "t2"]} errors={data.errors} stale={data.stale} />
      <SplitCoverage blocks={cur?.blocks} split={cur?.blocks_split} />
      <StatRow>
        <Tile
          label="Transactions"
          value={cur == null ? null : count(cur.transactions)}
          unit={cur == null ? undefined : `${count(cur.blocks)} blocks`}
          note={prev == null || cur == null ? null : `Window before: ${count(prev.transactions)} (${change(cur.transactions, prev.transactions) ?? "—"}). ${cur.blocks ? Math.round(cur.transactions / cur.blocks) : "—"} per block.`}
        />
        <Tile
          label="TRX burned"
          value={cur == null ? null : trx(cur.burned_trx_sun, 0)}
          note={s == null ? null : `Energy ${trx(sp(s, "energy_fee_sun"), 0)} · bandwidth ${trx(sp(s, "net_fee_sun"), 0)} · creation and other fees ${trx(sp(s, "other_fee_sun"), 0)}.`}
        />
        <Tile
          label="Energy from stake or rent"
          value={pct(sp(s, "energy_from_stake"), energyUsed)}
          unit="of energy used"
          note={energyUsed === 0 ? null : `The rest: ${pct(sp(s, "energy_from_origin"), energyUsed)} paid by contract owners, ${pct(sp(s, "energy_burned"), energyUsed)} burned in TRX — the room left for rentals.`}
        />
        <Tile
          label="USDT transfers"
          value={t2?.usdt_transfers == null ? null : count(t2.usdt_transfers)}
          unit={t2 == null ? undefined : `${usd(t2.usdt_moved)} moved`}
          note={
            needEnergy === 0
              ? `${count(t2?.senders)} senders.`
              : `Of contract calls: ${pct(sp(s, "energy_partial_tx") + sp(s, "energy_burned_tx"), needEnergy)} burned TRX for energy, ${pct(sp(s, "energy_covered_net_burned_tx"), needEnergy)} had energy but paid TRX for bandwidth. ${count(t2?.senders)} senders.`
          }
        />
      </StatRow>

      <Panel title="What the transactions were" subtitle="Each transaction once, by its first contract. 100 % = all transactions in the window.">
        <Stack
          parts={[
            { label: "USDT calls", value: sp(s, "tx_usdt"), tone: "bg-accent" },
            { label: "Other token transfers", value: sp(s, "tx_trc20_other"), tone: "bg-accent-text" },
            { label: "Other contract calls (swaps, games…)", value: sp(s, "tx_call_other"), tone: "bg-accent/45" },
            { label: "TRX transfers", value: sp(s, "tx_trx"), tone: "bg-save" },
            { label: "TRC-10 transfers", value: sp(s, "tx_trc10"), tone: "bg-ink-muted" },
            { label: "Stake and delegation", value: sp(s, "tx_resource"), tone: "bg-heat" },
            { label: "Other (votes, permissions…)", value: sp(s, "tx_other"), tone: "bg-line" },
          ]}
        />
      </Panel>

      <div class="grid gap-3 lg:grid-cols-2">
        <Panel title="Transactions that needed energy" subtitle={`Contract calls only. 100 % = ${count(needEnergy)} calls.`}>
          <Stack
            parts={[
              { label: "Energy covered (stake, rent, contract owner)", value: sp(s, "energy_covered_tx"), tone: "bg-save" },
              { label: "Partly burned: energy ran short", value: sp(s, "energy_partial_tx"), tone: "bg-heat" },
              { label: "Fully burned: no energy at all", value: sp(s, "energy_burned_tx"), tone: "bg-danger" },
            ]}
          />
          <p class="mt-2 text-[11.5px] text-ink-muted">
            Covered, but paid bandwidth: {count(sp(s, "energy_covered_net_burned_tx"))} · {pct(sp(s, "energy_covered_net_burned_tx"), sp(s, "energy_covered_tx")) ?? "—"} of covered.
          </p>
        </Panel>
        <Panel title="Transactions that needed no energy" subtitle={`TRX, TRC-10, staking, votes. 100 % = ${count(noEnergy)}.`}>
          <Stack
            parts={[
              { label: "Free or staked bandwidth", value: Math.max(0, noEnergy - sp(s, "noenergy_net_burned_tx")), tone: "bg-save" },
              { label: "Burned TRX for bandwidth", value: sp(s, "noenergy_net_burned_tx"), tone: "bg-danger" },
            ]}
          />
          <p class="mt-2 text-[11.5px] text-ink-muted">New accounts in the window: {count(cur?.new_accounts)}.</p>
        </Panel>
      </div>

      <Panel title="Resource operations" subtitle="Successful ones; TRX = stake behind them, whole TRX.">
        <Table
          rows={s == null ? 0 : 3}
          empty="No split counters in this window yet."
          head={
            <tr>
              <Th> </Th>
              <Th numeric>Energy · ops</Th>
              <Th numeric>Energy · TRX</Th>
              <Th numeric>Bandwidth · ops</Th>
              <Th numeric>Bandwidth · TRX</Th>
              <Th numeric>Ops vs window before</Th>
            </tr>
          }
        >
          <tr>
            <Td>
              Delegations<span class="block text-[11px] text-ink-muted">of them locked: {count(sp(s, "deleg_locked_ops"))}</span>
            </Td>
            <Td numeric>{count(sp(s, "deleg_energy_ops"))}</Td>
            <Td numeric>{count(sp(s, "deleg_energy_trx"))}</Td>
            <Td numeric>{count(sp(s, "deleg_bw_ops"))}</Td>
            <Td numeric>{count(sp(s, "deleg_bw_trx"))}</Td>
            <Td numeric>{change(sp(s, "deleg_energy_ops") + sp(s, "deleg_bw_ops"), sp(prev?.split, "deleg_energy_ops") + sp(prev?.split, "deleg_bw_ops")) ?? "—"}</Td>
          </tr>
          <tr>
            <Td>Undelegations</Td>
            <Td numeric>{count(sp(s, "undeleg_energy_ops"))}</Td>
            <Td numeric>{count(sp(s, "undeleg_energy_trx"))}</Td>
            <Td numeric>{count(sp(s, "undeleg_bw_ops"))}</Td>
            <Td numeric>{count(sp(s, "undeleg_bw_trx"))}</Td>
            <Td numeric>{change(sp(s, "undeleg_energy_ops") + sp(s, "undeleg_bw_ops"), sp(prev?.split, "undeleg_energy_ops") + sp(prev?.split, "undeleg_bw_ops")) ?? "—"}</Td>
          </tr>
          <tr>
            <Td>Freeze · unfreeze · withdraw</Td>
            <Td numeric class="whitespace-nowrap">
              + {count(sp(s, "freeze_trx"))} · − {count(sp(s, "unfreeze_trx"))} · {count(sp(s, "withdraw_unfreeze_trx"))}
            </Td>
            <Td numeric> </Td>
            <Td numeric>TRX staked, net</Td>
            <Td numeric>{count(sp(s, "freeze_trx") - sp(s, "unfreeze_trx"))}</Td>
            <Td numeric>—</Td>
          </tr>
        </Table>
      </Panel>

      <CollectorAndDays />
    </>
  );
}

/** The collector's state and the by-day table: the first Network page's blocks, window-independent. */
function CollectorAndDays() {
  const res = useResource((signal) => {
    void signal;
    return adminReads.network();
  }, []);
  if (res.data == null) return res.error !== null ? <ErrorNote error={res.error} onRetry={() => res.reload()} /> : <Spinner />;
  const data = res.data;
  if (!data.connected) return null;
  const c = data.collector;
  const asOf = `as of ${utc(data.as_of)}`;
  const behindBlocks = c?.lag_seconds == null ? null : Math.round(c.lag_seconds / 3);
  const callsShare = c?.calls_today == null ? null : c.calls_today / c.calls_cap;
  return (
    <>
      <Panel title="By day" subtitle="Full UTC days from the loaded history; a day not yet whole is marked partial. Delegations count energy and bandwidth together." action={
        <Button tone="ghost" disabled={data.days.length === 0} onClick={() => downloadCsv(`network-days-${data.as_of.slice(0, 10)}.csv`, daysCsv(data))}>
          CSV
        </Button>
      }>
        <Failed q="days" errors={data.errors} />
        <Table
          rows={data.days.length}
          empty="No full day loaded yet."
          head={
            <tr>
              <Th>Day (UTC)</Th>
              <Th numeric>Transactions</Th>
              <Th numeric>Transfers with energy</Th>
              <Th numeric>Burned TRX</Th>
              <Th numeric>TRX burned</Th>
              <Th numeric>USDT transfers</Th>
              <Th numeric>USDT moved</Th>
              <Th numeric>Delegations</Th>
              <Th numeric>New accounts</Th>
            </tr>
          }
        >
          {data.days.map((d) => (
            <tr key={d.day}>
              <Td>
                {d.day}
                {d.partial ? <span class="ml-1.5 text-[10px] text-warning">partial</span> : null}
              </Td>
              <Td numeric>{count(d.transactions)}</Td>
              <Td numeric>{count(d.transfers_with_energy)}</Td>
              <Td numeric>{count(d.burned_tx)}</Td>
              <Td numeric>{trx(d.burned_trx_sun, 0)}</Td>
              <Td numeric>{count(d.usdt_transfers)}</Td>
              <Td numeric>{d.usdt_moved === null ? "—" : `$ ${usdtWhole(d.usdt_moved)}`}</Td>
              <Td numeric>{count(d.delegations)}</Td>
              <Td numeric>{count(d.new_accounts)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
      <Panel title="Collector" subtitle="Is the analyzer keeping up, and does it have room.">
        <Failed q="collector" errors={data.errors} />
        <StatRow>
          <Tile
            label="Behind the chain"
            value={behindBlocks === null ? null : count(behindBlocks)}
            unit={c?.lag_seconds == null ? undefined : `blocks · ~${Math.round(c.lag_seconds / 60)} min`}
            tone={behindBlocks === null ? "neutral" : behindBlocks > 200 ? "danger" : behindBlocks > 40 ? "warning" : "good"}
            note={`19 of them are the confirmation wait. ${asOf}`}
          />
          <Tile label="History loaded" value={c?.history_pct == null ? null : `${c.history_pct} %`} note={c?.history_reached_at == null ? asOf : `Now at ${utc(c.history_reached_at)}. ${asOf}`} />
          <Tile
            label="Node requests today"
            value={c?.calls_today == null ? null : count(c.calls_today)}
            unit={c === null ? undefined : `of ${count(c.calls_cap)}`}
            tone={callsShare === null ? "neutral" : callsShare > 0.9 ? "warning" : "good"}
            note={`History loading pauses near the limit; new blocks always get their share. ${asOf}`}
          />
          <Tile
            label="Disk"
            value={c?.disk_used_pct == null ? null : `${c.disk_used_pct} %`}
            unit={c?.disk_total_bytes == null ? undefined : `of ${Math.round(c.disk_total_bytes / 1e9)} GB`}
            tone={c?.disk_used_pct == null ? "neutral" : c.disk_used_pct > 85 ? "danger" : c.disk_used_pct > 70 ? "warning" : "good"}
            note={`History loading pauses at 70 %, new blocks keep writing. ${asOf}`}
          />
        </StatRow>
      </Panel>
    </>
  );
}

/** 6-decimal USDT units (a decimal string) as whole dollars with group separators. */
function usdtWhole(units: string): string {
  const whole = units.length > 6 ? units.slice(0, -6) : "0";
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** The CSV carries the API's integers, not the rendered text (lib/csv.ts). */
export function daysCsv(data: NetworkView): string {
  return toCsv(
    ["day_utc", "partial", "blocks", "transactions", "transfers_with_energy", "burned_tx", "burned_trx_sun", "usdt_transfers", "usdt_moved_units", "delegations", "new_accounts"],
    data.days.map((d) => [d.day, d.partial, d.blocks, d.transactions, d.transfers_with_energy, d.burned_tx, d.burned_trx_sun, d.usdt_transfers, d.usdt_moved, d.delegations, d.new_accounts]),
  );
}

// ---- Demand ------------------------------------------------------------------------------------

type DemandRow = { transactions: number; split: SplitMap };
const METRICS: ReadonlyArray<{ id: string; label: string; of: (r: DemandRow) => number }> = [
  { id: "usdt", label: "USDT calls", of: (r) => sp(r.split, "tx_usdt") },
  { id: "energy", label: "Energy used", of: (r) => sp(r.split, "energy_from_stake") + sp(r.split, "energy_from_origin") + sp(r.split, "energy_burned") },
  { id: "burned", label: "Energy burned", of: (r) => sp(r.split, "energy_burned") },
  { id: "trx", label: "TRX burned", of: (r) => (sp(r.split, "energy_fee_sun") + sp(r.split, "net_fee_sun")) / 1e6 },
  { id: "deleg", label: "Delegations", of: (r) => sp(r.split, "deleg_energy_ops") },
  { id: "tx", label: "Transactions", of: (r) => r.transactions },
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DemandTab({ data, keep, onOpenCard }: TabContext) {
  const [metricId, setMetricId] = useState("usdt");
  const metric = METRICS.find((m) => m.id === metricId) ?? METRICS[0]!;
  const fromMs = Date.parse(data.window.from);
  const hours = Math.max(1, Math.ceil((Date.parse(data.window.to) - fromMs) / HOUR_MS));
  const rows = data.data.d1 ?? [];
  // Windows over 7 days come by UTC day (bucket_hours 24): one bar a day, no dashed line.
  const bucketH = rows.find((r) => r.current === 1)?.bucket_hours ?? 1;
  const bars = Math.max(1, Math.ceil(hours / bucketH));
  const cur = new Map(rows.filter((r) => r.current === 1).map((r) => [r.hour_index, r]));
  const before = new Map(rows.filter((r) => r.current === 0).map((r) => [r.hour_index, r]));
  const values = Array.from({ length: bars }, (_, i) => {
    const r = cur.get(i * bucketH);
    return r === undefined ? null : metric.of(r);
  });
  // The dashed line: the same hour a day earlier — the rows before the window for the first 24 hours, then the window's own.
  const prev = values.map((_, i) => {
    if (bucketH !== 1) return null;
    if (i < 24) {
      const r = before.get(i);
      return r === undefined ? null : metric.of(r);
    }
    return values[i - 24] ?? null;
  });
  const max = Math.max(1, ...values.map((v) => v ?? 0), ...prev.map((v) => v ?? 0));
  const label = (i: number) => {
    const at = new Date(fromMs + i * bucketH * HOUR_MS).toISOString();
    if (bucketH !== 1) return at.slice(0, 10);
    return hours <= 24 ? `${at.slice(11, 13)}` : `${at.slice(5, 10)} ${at.slice(11, 13)}`;
  };
  const d1Split = rows.filter((r) => r.current === 1).reduce((t, r) => t + r.blocks_split, 0);
  const d1Blocks = rows.filter((r) => r.current === 1).reduce((t, r) => t + r.blocks, 0);

  const week = data.data.d2 ?? [];
  const heat = new Map(week.map((r) => [`${r.weekday}:${r.hour}`, r.days === 0 ? 0 : metric.of(r) / r.days]));
  const heatMax = Math.max(1, ...heat.values());

  const delegators = (data.data.d3 ?? []).filter((r) => keep(r.address, r.delegated_sun / 1e6));
  const bucket = hours <= 48 ? 1 : 24;
  // Over 7 days the API sends one value per day (keyed by the day's hour index), so hour-of-day peaks are not known.
  const dailyOnly = (delegators[0]?.bucket_hours ?? 1) === 24;
  return (
    <>
      <div class="inline-flex flex-wrap gap-0.5 rounded-[12px] border border-line bg-surface p-[3px]" role="group" aria-label="Metric">
        {METRICS.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMetricId(m.id)}
            class={`rounded-[9px] px-2.5 py-1 text-[12px] ${m.id === metric.id ? "bg-surface-raised text-ink" : "text-ink-muted hover:text-ink"}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <Panel
        title={`${metric.label} by ${bucketH === 1 ? "hour" : "day"}`}
        subtitle={bucketH === 1 ? "Bars: the window by UTC hour; dashed line: the same hour a day earlier. What drives the price of energy." : "Bars: the window by UTC day."}
      >
        <Failed q="d1" errors={data.errors} stale={data.stale} />
        {metric.id === "tx" ? null : <SplitCoverage blocks={d1Blocks} split={d1Split} />}
        <div class="flex h-36 items-stretch gap-px" role="img" aria-label={`${metric.label} by UTC hour`}>
          {values.map((v, i) => (
            <div key={i} class="relative flex-1" title={`${label(i)}${bucketH === 1 ? ":00" : ""} UTC — ${v === null ? "no data" : count(Math.round(v))}`}>
              <div class="absolute inset-x-0 bottom-0 rounded-t-[3px] border-t-2 border-accent bg-accent/15" style={{ height: `${v === null ? 0 : (100 * v) / max}%` }} />
              {prev[i] == null ? null : <div class="absolute inset-x-0 border-t-2 border-dashed border-ink-muted" style={{ bottom: `${(100 * prev[i]!) / max}%` }} />}
            </div>
          ))}
        </div>
        <div class="num mt-1 flex justify-between text-[10px] text-ink-muted">
          {[0, Math.floor(bars / 4), Math.floor(bars / 2), Math.floor((3 * bars) / 4)].map((i) => (
            <span key={i}>{label(i)}</span>
          ))}
          <span>now</span>
        </div>
      </Panel>
      <Panel title="Week by hour" subtitle="Average per hour of the day, last 4 full weeks, whatever the window. Darker = more. Shows when the network is busy every week.">
        <Failed q="d2" errors={data.errors} stale={data.stale} />
        <div class="num grid gap-[2px] text-[10px] text-ink-muted" style={{ gridTemplateColumns: "34px repeat(24, minmax(0, 1fr))" }}>
          {WEEKDAYS.map((day, di) => (
            <Fragment key={day}>
              <span>{day}</span>
              {Array.from({ length: 24 }, (_, h) => {
                const v = heat.get(`${di + 1}:${h}`);
                return (
                  <i
                    key={`${day}${h}`}
                    class="block h-[18px] rounded-[3px] bg-accent"
                    style={{ opacity: v === undefined ? 0.05 : Math.max(0.08, v / heatMax) }}
                    title={`${day} ${hh(h)}:00 UTC — ${v === undefined ? "no data" : count(Math.round(v))}`}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </Panel>
      <Panel title="Each delegator by hour" subtitle={`Energy TRX handed out, top 20 by TRX delegated in the window; ${bucket === 1 ? "by hour" : "by day"}. Addresses of one service fold into one row on the Providers tab.`}>
        <Failed q="d3" errors={data.errors} stale={data.stale} />
        <Table
          rows={delegators.length}
          empty="No energy delegations in this window (or none match the filter)."
          head={
            <tr>
              <Th>Delegator</Th>
              <Th numeric>Delegations</Th>
              <Th numeric>TRX delegated</Th>
              <Th numeric>Receivers</Th>
              <Th>{bucket === 1 ? "By hour" : "By day"}</Th>
              <Th numeric>Peak hour</Th>
            </tr>
          }
        >
          {delegators.map((r) => {
            const byIndex = Array.from({ length: hours }, (_, i) => r.sun_by_hour_index[String(i)] ?? 0);
            const series = bucket === 1 ? byIndex : Array.from({ length: Math.ceil(hours / 24) }, (_, d) => byIndex.slice(d * 24, d * 24 + 24).reduce((t, v) => t + v, 0));
            const byHourOfDay = Array.from({ length: 24 }, () => 0);
            byIndex.forEach((v, i) => (byHourOfDay[new Date(fromMs + i * HOUR_MS).getUTCHours()]! += v));
            const peak = byHourOfDay.indexOf(Math.max(...byHourOfDay));
            return (
              <tr key={r.address}>
                <Td>
                  <AddressCell value={r.address} onOpenCard={onOpenCard} />
                </Td>
                <Td numeric>{count(r.delegations)}</Td>
                <Td numeric>{trxCompact(r.delegated_sun)}</Td>
                <Td numeric>{count(r.receiver_count)}</Td>
                <Td>
                  <Spark values={series} />
                </Td>
                <Td numeric>{dailyOnly ? "—" : `${hh(peak)} UTC`}</Td>
              </tr>
            );
          })}
        </Table>
      </Panel>
    </>
  );
}

// ---- Providers ---------------------------------------------------------------------------------

/** New vs returning receivers of one provider row: a heavier read (all delegation history), so only on a click. */
function ProviderReceivers({ owners, view }: { owners: string[]; view: ViewState }) {
  const res = useResource(
    (signal) => {
      void signal;
      return adminReads.networkTab("provider", { ...windowQuery(view), owners: owners.join(",") });
    },
    [owners.join(","), view.window, view.from, view.to],
  );
  if (res.data == null) return res.error !== null ? <span class="text-danger">failed</span> : <span class="text-ink-muted">…</span>;
  const failed = res.data.errors["p2"];
  const r = res.data.data.p2?.[0];
  if (failed !== undefined || r === undefined) return <span class="text-warning" title={failed}>failed</span>;
  return (
    <>
      +{count(r.new_receivers)} · {count(r.returning_receivers)}
    </>
  );
}

function ProvidersTab({ data, view, keep, onOpenCard }: TabContext) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const rows = (data.data.p1 ?? []).filter((r) => keep(r.provider, r.delegated_sun / 1e6));
  return (
    <Panel
      title="Energy providers"
      subtitle="One row per service: the pool's own registry label, else the service whose controller key the pool granted, else the delegator's address. Share = of all TRX delegated in the window. TRX delegated is turnover, not stake: the same stake counts each time it is delegated again (a pool re-delegating 4.6M TRX many times an hour sums to billions). New · returning: receivers active in the window whose first energy from that provider ever came inside it, and the rest — counted on a click."
    >
      <Failed q="p1" errors={data.errors} stale={data.stale} />
      <Table
        rows={rows.length}
        empty="No energy delegations in this window (or none match the filter)."
        head={
          <tr>
            <Th>Provider</Th>
            <Th numeric>Share</Th>
            <Th numeric>
              <span title="The same stake counts each time it is delegated again">TRX delegated (sum of all delegations)</span>
            </Th>
            <Th numeric>Avg delegation, TRX</Th>
            <Th numeric>Orders</Th>
            <Th numeric>Receivers</Th>
            <Th numeric>New · returning</Th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.provider}>
            <Td>
              <PartyCell name={r.provider} sub={TRON_ADDRESS.test(r.provider) ? undefined : `${count(r.addresses)} addresses`} onOpenCard={onOpenCard} />
            </Td>
            <Td numeric>{r.share_pct == null ? "—" : `${r.share_pct} %`}</Td>
            <Td numeric>{trxCompact(r.delegated_sun)}</Td>
            <Td numeric>{r.delegations > 0 ? trxCompact(r.delegated_sun / r.delegations) : "—"}</Td>
            <Td numeric>{count(r.delegations)}</Td>
            <Td numeric>{count(r.receivers)}</Td>
            <Td numeric>
              {opened.has(r.provider) ? (
                <ProviderReceivers owners={r.owner_addresses} view={view} />
              ) : (
                <button type="button" class="text-[11px] text-accent-text hover:underline" onClick={() => setOpened((s) => new Set(s).add(r.provider))}>
                  count
                </button>
              )}
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

// ---- Burners -----------------------------------------------------------------------------------

function BurnersTab({ data, keep, onOpenCard }: TabContext) {
  const rows = (data.data.b1 ?? []).filter((r) => keep(r.address, r.burned_trx_sun / 1e6));
  const trend = new Map<string, Map<string, number>>();
  for (const r of data.data.b2 ?? []) {
    const m = trend.get(r.address) ?? new Map<string, number>();
    m.set(r.day, r.burned_trx_sun);
    trend.set(r.address, m);
  }
  const today = Date.parse(`${data.as_of.slice(0, 10)}T00:00:00Z`);
  const days = Array.from({ length: 7 }, (_, i) => new Date(today - (6 - i) * 86_400_000).toISOString().slice(0, 10));
  return (
    <Panel
      title="Who burns instead of renting"
      subtitle='Sales leads: addresses that burned at least 20 TRX in some hour of the window, top 50 by TRX burned. "Had energy, 7 d" = used own or rented energy on some call in the last 7 days.'
    >
      <Failed q={["b1", "b2"]} errors={data.errors} stale={data.stale} />
      <Table
        rows={rows.length}
        empty="No burns in this window (or none match the filter)."
        head={
          <tr>
            <Th>Address</Th>
            <Th numeric>TRX burned</Th>
            <Th numeric>Calls</Th>
            <Th numeric>Energy burned</Th>
            <Th numeric>Partly burned calls</Th>
            <Th>First burn in window – last</Th>
            <Th>Had energy, 7 d</Th>
            <Th numeric>7 d trend</Th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.address}>
            <Td>
              <AddressCell value={r.address} onOpenCard={onOpenCard} />
            </Td>
            <Td numeric>{trx(r.burned_trx_sun, 0)}</Td>
            <Td numeric>{count(r.calls)}</Td>
            <Td numeric>{r.energy_burned_pct} %</Td>
            <Td numeric>{r.partial_calls_pct} %</Td>
            <Td mono>
              {dayHour(r.first_burn_hour)} – {dayHour(r.last_burn_hour)}
            </Td>
            <Td>{r.had_energy_7d ? <Pill value={r.partial_calls_pct > 0 ? "yes, short" : "yes"} tone="attention" /> : <Pill value="no" tone="neutral" />}</Td>
            <Td numeric>
              <Spark values={days.map((d) => trend.get(r.address)?.get(d) ?? 0)} />
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

// ---- USDT --------------------------------------------------------------------------------------

function UsdtTab({ data, keep, onOpenCard }: TabContext) {
  const table = (rows: UsdtPartyRow[] | undefined, peers: string) => {
    const kept = (rows ?? []).filter((r) => keep(r.address, usdNumber(r.usdt_moved)));
    return (
      <Table
        rows={kept.length}
        empty="No USDT transfers in this window (or none match the filter)."
        head={
          <tr>
            <Th>Address</Th>
            <Th numeric>Transfers</Th>
            <Th numeric>USDT</Th>
            <Th numeric>{peers}</Th>
          </tr>
        }
      >
        {kept.map((r) => (
          <tr key={r.address}>
            <Td>
              <AddressCell value={r.address} onOpenCard={onOpenCard} />
            </Td>
            <Td numeric>{count(r.transfers)}</Td>
            <Td numeric>{usd(r.usdt_moved)}</Td>
            <Td numeric>{r.distinct_exact === false ? "≈ " : ""}{count(r.peer_count)}</Td>
          </tr>
        ))}
      </Table>
    );
  };
  return (
    <div class="grid gap-3 lg:grid-cols-2">
      <Panel title="Senders" subtitle="Top 50 by USDT sent in the window.">
        <Failed q="u1" errors={data.errors} stale={data.stale} />
        {table(data.data.u1, "Receivers")}
      </Panel>
      <Panel title="Receivers" subtitle="Top 50 by USDT received; a service's deposit address shows here.">
        <Failed q="u2" errors={data.errors} stale={data.stale} />
        {table(data.data.u2, "Senders")}
      </Panel>
    </div>
  );
}

// ---- Activations -------------------------------------------------------------------------------

function ActivationsTab({ data, keep, onOpenCard }: TabContext) {
  const rows = (data.data.a1 ?? []).filter((r) => keep(r.address, null));
  return (
    <Panel title="Who activates new wallets" subtitle="Activator addresses by accounts created in the window, top 50: by AccountCreate, or by a transfer that paid the creation fee.">
      <Failed q="a1" errors={data.errors} stale={data.stale} />
      <Table
        rows={rows.length}
        empty="No activations in this window (or none match the filter)."
        head={
          <tr>
            <Th>Activator</Th>
            <Th numeric>Activated</Th>
            <Th numeric>By AccountCreate</Th>
            <Th numeric>By transfer</Th>
            <Th>First · last</Th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.address}>
            <Td>
              <AddressCell value={r.address} onOpenCard={onOpenCard} />
            </Td>
            <Td numeric>{count(r.activated)}</Td>
            <Td numeric>{count(r.by_account_create)}</Td>
            <Td numeric>{count(r.by_transfer)}</Td>
            <Td mono>
              {utc(r.first_at)} · {utc(r.last_at)}
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

// ---- Watch -------------------------------------------------------------------------------------

const WATCH_ROLES: ReadonlyArray<{ id: WatchTargetRow["role"] | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "client", label: "Dashboard clients" },
  { id: "payer", label: "Pay-in buyers" },
  { id: "activator", label: "Activators" },
  { id: "parent", label: "Parent wallets" },
];

const ROLE_LABEL: Record<WatchTargetRow["role"], string> = {
  client: "client",
  payer: "pay-in buyer",
  activator: "activator",
  parent: "parent",
};

/** USDT counts ×3.4 TRX for ranking only, as in the SQL. */
const weekEq = (r: WatchTargetRow) => r.week_trx_sun / 1e6 + (3.4 * r.week_usdt_units) / 1e6;

/**
 * Watch (docs/product/chain-watch.md, owner 2026-10-23): who buys energy from other venues, found as they pay — buyers
 * on the venues' pay-in wallets, clients topping up their dashboard deposits, the wallets behind them (activator,
 * parent). Always the last 7 days, whatever the window above. Collection only: nothing is sent from here.
 */
function WatchTab({ data, keep, onOpenCard }: TabContext) {
  const [role, setRole] = useState<WatchTargetRow["role"] | "all">("all");
  const [decile, setDecile] = useState(0);
  const all = data.data.w1 ?? [];
  const rows = all.filter(
    (r) =>
      (role === "all" || r.role === role) &&
      (decile === 0 || r.decile === decile) &&
      keep(`${r.address} ${r.venues.join(" ")} ${r.of.join(" ")}`, r.role === "client" || r.role === "payer" ? weekEq(r) : null),
  );
  const byRole = (x: WatchTargetRow["role"]) => all.filter((r) => r.role === x).length;
  const wallets = data.data.w2 ?? [];
  const deposits = data.data.w3 ?? [];
  return (
    <div class="space-y-3">
      <Panel
        title="Targets, last 7 days"
        subtitle="Clients top up their dashboard deposit at a venue; pay-in buyers pay a venue's wallet directly. Activators and parents are the wallets behind them. Deciles per role: 10 = the biggest spenders. Spend is what they paid the venue this week; USDT ranks as 3.4 TRX."
      >
        <Failed q="w1" errors={data.errors} stale={data.stale} />
        <div class="mb-2.5 flex flex-wrap items-center gap-2">
          <div class="inline-flex gap-0.5 rounded-[12px] border border-line bg-surface p-[3px]" role="group" aria-label="Role">
            {WATCH_ROLES.map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setRole(x.id)}
                class={`rounded-[9px] px-2.5 py-1 text-[12px] ${role === x.id ? "bg-surface-raised text-ink" : "text-ink-muted hover:text-ink"}`}
              >
                {x.label} {count(x.id === "all" ? all.length : byRole(x.id))}
              </button>
            ))}
          </div>
          <select class={`${inputClass} w-auto`} value={String(decile)} onChange={(e) => setDecile(Number((e.currentTarget as HTMLSelectElement).value))} aria-label="Decile">
            <option value="0">All deciles</option>
            {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((d) => (
              <option key={d} value={String(d)}>
                D{d}
              </option>
            ))}
          </select>
          <Button
            tone="ghost"
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv(
                `watch-targets-${data.as_of.slice(0, 10)}.csv`,
                toCsv(
                  ["role", "address", "venues", "week_trx_sun", "week_usdt_units", "payments", "decile", "first_seen", "of", "activator", "activator_kind", "token_sent_at"],
                  rows.map((r) => [r.role, r.address, r.venues.join(" "), r.week_trx_sun, r.week_usdt_units, r.payments, r.decile, r.first_seen, r.of.join(" "), r.activator, r.activator_kind ?? "", r.token_sent_at ?? ""]),
                ),
              )
            }
          >
            Download CSV
          </Button>
        </div>
        <Table
          rows={rows.length}
          empty="Nobody yet (or nobody matches the filter). The watch fills as venues' wallets get payments."
          head={
            <tr>
              <Th>Address</Th>
              <Th>Role</Th>
              <Th>Venue</Th>
              <Th numeric>Paid this week</Th>
              <Th numeric>Payments</Th>
              <Th numeric>Decile</Th>
              <Th>Activator</Th>
              <Th>First seen</Th>
              <Th>Token sent</Th>
            </tr>
          }
        >
          {rows.slice(0, 1000).map((r) => (
            <tr key={`${r.role}:${r.address}`}>
              <Td>
                <AddressCell value={r.address} sub={r.of.length > 0 ? `behind ${r.of.length === 1 ? r.of[0] : `${r.of.length} targets`}` : undefined} onOpenCard={onOpenCard} />
              </Td>
              <Td>{ROLE_LABEL[r.role]}</Td>
              <Td>{r.venues.join(", ") || "—"}</Td>
              <Td numeric>
                {r.role === "client" || r.role === "payer" ? (
                  <>
                    {trx(r.week_trx_sun, 0)}
                    {r.week_usdt_units > 0 ? <span class="block text-[11px] text-ink-muted">+ {usd(String(r.week_usdt_units))}</span> : null}
                  </>
                ) : r.role === "parent" ? (
                  <span class="text-ink-muted">sent {trx(r.week_trx_sun, 0)}</span>
                ) : (
                  "—"
                )}
              </Td>
              <Td numeric>{r.payments > 0 ? count(r.payments) : "—"}</Td>
              <Td numeric>{r.decile > 0 ? `D${r.decile}` : "—"}</Td>
              <Td>
                {TRON_ADDRESS.test(r.activator) ? <Address value={r.activator} /> : null}
                {r.role === "activator" ? null : <span class="block text-[11px] text-ink-muted">{r.activator_kind ?? ""}</span>}
              </Td>
              <Td mono>{r.first_seen.startsWith("1970") ? "—" : utc(r.first_seen)}</Td>
              <Td>{r.token_sent_at ? <Pill value={utc(r.token_sent_at)} tone="neutral" /> : <span class="text-ink-muted">no</span>}</Td>
            </tr>
          ))}
        </Table>
        {rows.length > 1000 ? <p class="mt-2 text-[11.5px] text-ink-muted">Showing 1,000 of {count(rows.length)}; the CSV has all of them.</p> : null}
      </Panel>
      <Note>Sending the token to the watch's buyers and clients is switched on the tenergy.app page.</Note>
      <WatchAddForm venues={[...new Set(wallets.map((w) => w.venue))].sort()} />
      <div class="grid gap-3 lg:grid-cols-2">
        <Panel title="Watched venue wallets" subtitle="Pay-in wallets, collectors and the wallets collectors withdraw to (anchors). auto = found by the watch: a rotated collector or a new withdrawal wallet.">
          <Failed q="w2" errors={data.errors} stale={data.stale} />
          <Table
            rows={wallets.length}
            empty="The watch list is empty."
            head={
              <tr>
                <Th>Address</Th>
                <Th>Kind</Th>
                <Th>Venue</Th>
                <Th>Added</Th>
              </tr>
            }
          >
            {wallets.map((w) => (
              <tr key={w.address}>
                <Td>
                  <AddressCell value={w.address} sub={w.note || undefined} onOpenCard={onOpenCard} />
                </Td>
                <Td>{w.kind === "payin" ? "pay-in" : w.kind}</Td>
                <Td>{w.venue}</Td>
                <Td mono>
                  {utc(w.added_at)} {w.origin === "auto" || w.origin === "replay" ? <Pill value="auto" tone="attention" /> : null}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel title="Client deposit addresses" subtitle="Deposits the watch found: senders that sweep into a known collector.">
          <Failed q="w3" errors={data.errors} stale={data.stale} />
          <Table
            rows={deposits.length}
            empty="No deposits found yet."
            head={
              <tr>
                <Th>Venue</Th>
                <Th>Found by</Th>
                <Th numeric>Deposits</Th>
                <Th>Last found</Th>
              </tr>
            }
          >
            {deposits.map((d) => (
              <tr key={`${d.venue}:${d.origin}`}>
                <Td>{d.venue}</Td>
                <Td>{d.origin === "replay" ? "the week's history" : d.origin === "auto" ? "live" : d.origin}</Td>
                <Td numeric>{count(d.deposits)}</Td>
                <Td mono>{utc(d.last_added)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </div>
  );
}

/** Add a venue's pay-in wallet, or one client deposit address: the analyzer finds the collector and the rest itself. */
function WatchAddForm({ venues }: { venues: string[] }) {
  const [address, setAddress] = useState("");
  const [kind, setKind] = useState<"payin" | "deposit">("deposit");
  const [venue, setVenue] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<{ busy: boolean; done: string | null; error: string | null }>({ busy: false, done: null, error: null });
  const ok = TRON_ADDRESS.test(address.trim()) && venue.trim() !== "";
  return (
    <Panel
      title="Add to the watch"
      subtitle="A pay-in wallet: everyone who pays it is a buyer. A deposit address: the watch finds the venue's collector from it, then the venue's other deposits, their clients and where the collector withdraws. Within a minute the analyzer replays the stored week for it."
    >
      <form
        class="flex flex-wrap items-center gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!ok || state.busy) return;
          setState({ busy: true, done: null, error: null });
          try {
            await adminWrites.addWatchAddress({ address: address.trim(), kind, venue: venue.trim(), note: note.trim() });
            setState({ busy: false, done: `${address.trim()} added`, error: null });
            setAddress("");
            setNote("");
          } catch (error) {
            setState({ busy: false, done: null, error: error instanceof Error ? error.message : "not added" });
          }
        }}
      >
        <input class={`${inputClass} w-80`} placeholder="T-address" value={address} onInput={(e) => setAddress((e.currentTarget as HTMLInputElement).value)} aria-label="Address" />
        <select class={`${inputClass} w-auto`} value={kind} onChange={(e) => setKind((e.currentTarget as HTMLSelectElement).value as "payin" | "deposit")} aria-label="Kind">
          <option value="deposit">client deposit</option>
          <option value="payin">pay-in wallet</option>
        </select>
        <input class={`${inputClass} w-40`} list="watch-venues" placeholder="Venue" value={venue} onInput={(e) => setVenue((e.currentTarget as HTMLInputElement).value)} aria-label="Venue" />
        <datalist id="watch-venues">
          {venues.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
        <input class={`${inputClass} w-64`} placeholder="Note (optional)" value={note} onInput={(e) => setNote((e.currentTarget as HTMLInputElement).value)} aria-label="Note" />
        <Button type="submit" disabled={!ok || state.busy}>
          {state.busy ? "Adding…" : "Add"}
        </Button>
        {state.done === null ? null : <span class="text-[11.5px] text-ink-muted">{state.done}</span>}
      </form>
      {state.error === null ? null : <Note tone="warning">{state.error}</Note>}
    </Panel>
  );
}

// ---- Address card ------------------------------------------------------------------------------

/** A flow's amount: TRX in SUN and USDT in 6-decimal units are shown in their unit; another token's raw units as they are. */
function flowAmount(token: string, units: string): string {
  if (token === USDT_CONTRACT) return usd(units);
  if (token.toLowerCase() === "trx") return trx(Number(units), 1);
  return count(Number(units));
}

function AddressTab({ data, keep, onOpenCard }: TabContext) {
  const address = data.address ?? "";
  const label = data.data.c1Label?.[0];
  const act = data.data.c1?.[0];
  const days = data.data.c2 ?? [];
  const stake = days.reduce((t, r) => t + r.stake_energy, 0);
  const burned = days.reduce((t, r) => t + r.burned_energy, 0);
  const burnedSun = days.reduce((t, r) => t + r.burned_trx_sun, 0);
  const delegators = (data.data.c3 ?? []).filter((r) => keep(r.delegator, r.net_sun / 1e6));
  const flows = (data.data.c4 ?? []).filter((r) => keep(r.token_or_op, null));
  return (
    <>
      <Panel title="Address card" subtitle="Everything we know about one address in the window. Delegations received are all loaded history.">
        <div class="mb-3">
          <Address value={address} />
        </div>
        <Failed q={["c1", "c1Label", "c2"]} errors={data.errors} stale={data.stale} />
        <StatRow>
          <Tile label="Label" value={label === undefined ? "unlabelled" : label.service} unit={label?.role} note={label === undefined ? "Not in the address registry yet." : `${label.source}${label.note ? ` · ${label.note}` : ""}`} />
          <Tile label="Activated" value={act === undefined ? null : utc(act.activated_at).slice(0, 10)} note={act === undefined ? "Activation not in the loaded history." : `By ${act.activator} (${act.rule}).`} />
          <Tile
            label="Energy used"
            value={stake + burned === 0 ? null : count(stake + burned)}
            note={stake + burned === 0 ? "No calls in the window." : `From stake or rent ${pct(stake, stake + burned)}, burned ${pct(burned, stake + burned)}: ${trx(burnedSun, 0)}.`}
          />
          <Tile label="Energy delegators" value={count(data.data.c3?.length ?? null)} note="Addresses that ever delegated energy to it (top 100 by last delegation)." />
        </StatRow>
      </Panel>
      <Panel title="Energy by day" subtitle="Calls, energy from stake or rent, energy burned and TRX burned.">
        <Table
          rows={days.length}
          empty="No calls by this address in the window."
          head={
            <tr>
              <Th>Day (UTC)</Th>
              <Th numeric>Calls</Th>
              <Th numeric>Stake energy</Th>
              <Th numeric>Burned energy</Th>
              <Th numeric>TRX burned</Th>
            </tr>
          }
        >
          {days.map((r) => (
            <tr key={r.day}>
              <Td mono>{r.day}</Td>
              <Td numeric>{count(r.calls)}</Td>
              <Td numeric>{count(r.stake_energy)}</Td>
              <Td numeric>{count(r.burned_energy)}</Td>
              <Td numeric>{trx(r.burned_trx_sun, 0)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
      <div class="grid gap-3 xl:grid-cols-2">
        <Panel
          title="Who delegated energy to it"
          subtitle="Net TRX still delegated and when."
          action={
            <Button
              tone="ghost"
              disabled={delegators.length === 0}
              onClick={() => downloadCsv(`delegators-${address}.csv`, toCsv(["delegator", "net_sun", "first_at", "last_at"], delegators.map((r) => [r.delegator, r.net_sun, r.first_at, r.last_at])))}
            >
              CSV
            </Button>
          }
        >
          <Failed q="c3" errors={data.errors} stale={data.stale} />
          <Table
            rows={delegators.length}
            empty="Nobody delegated energy to this address in the loaded history."
            head={
              <tr>
                <Th>Delegator</Th>
                <Th numeric>Net TRX</Th>
                <Th>First · last</Th>
              </tr>
            }
          >
            {delegators.map((r) => (
              <tr key={r.delegator}>
                <Td>
                  <AddressCell value={r.delegator} onOpenCard={onOpenCard} />
                </Td>
                <Td numeric>{trx(r.net_sun, 0)}</Td>
                <Td mono>
                  {utc(r.first_at)} · {utc(r.last_at)}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel
          title="Flows by day"
          subtitle="Transfers by token and direction (TRX from 0.1). Other tokens' amounts are in their raw units."
          action={
            <Button
              tone="ghost"
              disabled={flows.length === 0}
              onClick={() => downloadCsv(`flows-${address}.csv`, toCsv(["day", "token_or_op", "direction", "transfers", "amount_units"], flows.map((r) => [r.day, r.token_or_op, r.direction, r.transfers, r.amount])))}
            >
              CSV
            </Button>
          }
        >
          <Failed q="c4" errors={data.errors} stale={data.stale} />
          <Table
            rows={flows.length}
            empty="No transfers by this address in the window."
            head={
              <tr>
                <Th>Day</Th>
                <Th>Token</Th>
                <Th>Direction</Th>
                <Th numeric>Transfers</Th>
                <Th numeric>Amount</Th>
              </tr>
            }
          >
            {flows.map((r) => (
              <tr key={`${r.day}${r.token_or_op}${r.direction}`}>
                <Td mono>{r.day}</Td>
                <Td>{r.token_or_op === USDT_CONTRACT ? "USDT" : TRON_ADDRESS.test(r.token_or_op) ? <Address value={r.token_or_op} /> : r.token_or_op}</Td>
                <Td>{r.direction}</Td>
                <Td numeric>{count(r.transfers)}</Td>
                <Td numeric>{flowAmount(r.token_or_op, r.amount)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </>
  );
}

// ---- Reports -----------------------------------------------------------------------------------

function ReportsTab() {
  return (
    <Panel title="Reports" subtitle="Not built yet: the reports planned on the same tables, any period, CSV and a printable page.">
      <Table
        rows={4}
        empty=""
        head={
          <tr>
            <Th>Report</Th>
            <Th>What it answers</Th>
            <Th>Period</Th>
          </tr>
        }
      >
        <tr>
          <Td>Monthly network</Td>
          <Td>Demand, burns, rental share, providers' shares and their change</Td>
          <Td>calendar month</Td>
        </tr>
        <tr>
          <Td>Provider</Td>
          <Td>One service: energy, orders, receivers, delegators joining and leaving</Td>
          <Td>any</Td>
        </tr>
        <tr>
          <Td>Leads</Td>
          <Td>Burners above a threshold that do not rent, with their hours</Td>
          <Td>7 / 30 d</Td>
        </tr>
        <tr>
          <Td>Promo list</Td>
          <Td>Addresses by spend on chosen tokens and contracts</Td>
          <Td>any</Td>
        </tr>
      </Table>
    </Panel>
  );
}
